import { useNavigate } from "react-router";
import type { CSSProperties } from "react";
import { useWorkspaceInfo } from "../../lib/hooks/use-kuvio";
import { useIsOwner } from "../hooks/use-is-owner";
import { useChatScope } from "../hooks/use-chat-scope";
import {
  useBackfillMemories,
  useMemoryBackfillStatus,
} from "../hooks/use-memory-backfill-status";
import { useLanguageTheme } from "../hooks/use-language-theme";
import { useToolResponseStats } from "../hooks/use-tool-response-stats";
import { useMcpPrompts } from "../hooks/use-mcp-prompts";
import { showToast } from "../components/primitives";
import { ThemeCard } from "../components/theme/ThemeCard";
import { LANGUAGE_THEMES, LANGUAGE_THEME_NAMES } from "../lib/themed-labels";

const SECTIONS = [
  { icon: "ti-palette", title: "Workspace identity", desc: "Name, icon, and brand colour." },
  { icon: "ti-plug-connected", title: "Connections", desc: "MCP connections and API keys." },
  { icon: "ti-credit-card", title: "Billing", desc: "Plan, usage, and invoices." },
  { icon: "ti-alert-triangle", title: "Danger zone", desc: "Archive, transfer, or delete this workspace." },
];

export function WorkspaceSettingsPage() {
  const navigate = useNavigate();
  const workspace = useWorkspaceInfo();
  const rootNodeId = workspace.data?.root_node_id as string | undefined;
  const isOwner = useIsOwner();
  // Sprint 23.9 (Fix 6 / S23.9.22) — explicit workspace scope (was a fall-through).
  useChatScope({ kind: "workspace", label: "Settings" }, null, []);

  const container: CSSProperties = {
    maxWidth: 560,
    margin: "0 auto",
    padding: "48px 0",
    fontFamily: "var(--font-sans)",
  };

  const card: CSSProperties = {
    padding: "16px 20px",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-md)",
    background: "var(--color-background-primary)",
    opacity: 0.5,
  };

  return (
    <div style={container}>
      <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 8 }}>Workspace settings</h1>
      <p style={{ fontSize: 14, color: "var(--color-text-secondary)", marginBottom: 24, lineHeight: 1.5 }}>
        This is where you'll configure workspace-level options — icon, connections, billing, and danger zone. Coming soon.
      </p>

      {/* Sprint 17 — MCP endpoints: a live, clickable card above the placeholders. */}
      <button
        onClick={() => navigate("/settings/endpoints")}
        style={{
          ...card,
          opacity: 1,
          cursor: "pointer",
          textAlign: "left",
          width: "100%",
          marginBottom: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <i className="ti ti-plug" style={{ fontSize: 16, color: "var(--color-text-secondary)" }} aria-hidden="true" />
          <span style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>MCP endpoints</span>
          <i className="ti ti-arrow-right" style={{ fontSize: 14, color: "var(--color-text-tertiary)", marginLeft: "auto" }} aria-hidden="true" />
        </div>
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)", paddingLeft: 26 }}>
          Copy per-route MCP URLs (full, admin, content, lite, per-connection) to attach to each host.
        </div>
      </button>

      {/* Sprint 23.7 — Members, moved under Settings (was a top-nav slot). */}
      <button
        onClick={() => navigate("/settings/members")}
        style={{ ...card, opacity: 1, cursor: "pointer", textAlign: "left", width: "100%", marginBottom: 12 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <i className="ti ti-users" style={{ fontSize: 16, color: "var(--color-text-secondary)" }} aria-hidden="true" />
          <span style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>Members</span>
          <i className="ti ti-arrow-right" style={{ fontSize: 14, color: "var(--color-text-tertiary)", marginLeft: "auto" }} aria-hidden="true" />
        </div>
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)", paddingLeft: 26 }}>
          Invite teammates, manage roles, and remove access.
        </div>
      </button>

      {/* №16 D-WF13 — legacy Workflows settings hub card retired. */}

      {/* Sprint 23 — Appearance: workspace-level language theme picker. */}
      <AppearanceSection cardStyle={card} />

      {/* Sprint 20a — memory backfill: owner-only live trigger for the workspace-wide
          semantic-search vectorisation sweep. */}
      {isOwner && <MemoryBackfillCard cardStyle={card} />}

      {/* Sprint 24.9 — MCP usage: owner-only top tools by response size. */}
      {isOwner && <McpUsageCard cardStyle={card} />}

      {/* Sprint 24.10 — Context bundles: the Kuvio-blessed bundles a host can fetch
          in one call. Catalogue visible to all; invocation counts owner-only. */}
      <ContextBundlesCard cardStyle={card} isOwner={isOwner} />

      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 32 }}>
        {SECTIONS.map((s) => (
          <div key={s.title} style={card}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <i className={`ti ${s.icon}`} style={{ fontSize: 16, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
              <span style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>{s.title}</span>
            </div>
            <div style={{ fontSize: 13, color: "var(--color-text-secondary)", paddingLeft: 26 }}>{s.desc}</div>
          </div>
        ))}
      </div>

      <button
        onClick={() => rootNodeId ? navigate(`/agents/${rootNodeId}`) : navigate(-1)}
        style={{ fontSize: 13 }}
      >
        <i className="ti ti-arrow-left" style={{ fontSize: 14, marginRight: 6 }} aria-hidden="true" />
        Back to workspace home
      </button>
    </div>
  );
}

/**
 * Sprint 20a — Memory backfill panel. Shows how many memories are indexed for
 * semantic search and lets the owner vectorise the rest in one click. Minimal v1:
 * a single button + inline status/result text (no progress bar — backfill of an
 * SMB workspace completes in well under a minute, and the auto-sweep handles the
 * one-time post-deploy case in the background).
 */
function MemoryBackfillCard({ cardStyle }: { cardStyle: CSSProperties }) {
  const status = useMemoryBackfillStatus(true);
  const backfill = useBackfillMemories();

  const s = status.data;
  const pending = s?.pending_count ?? 0;
  const inProgress = (s?.in_progress ?? false) || backfill.isPending;
  const indexUnavailable = s != null && !s.index_bound;
  const canBackfill = !!s && s.index_bound && pending > 0 && !inProgress;

  let statusText: string;
  if (status.isLoading) statusText = "Checking memory index…";
  else if (status.isError) statusText = "Couldn't load backfill status.";
  else if (indexUnavailable) statusText = "Semantic memory index isn't configured for this workspace.";
  else if (inProgress) statusText = "Backfilling memories… this runs in the background.";
  else if (pending === 0) statusText = `All ${s?.total ?? 0} memories indexed for semantic search.`;
  else statusText = `${pending} of ${s?.total ?? 0} memories pending backfill.`;

  const result = backfill.data;
  const resultText = backfill.isError
    ? backfill.error.message
    : result
      ? result.total_targeted === 0
        ? "Already fully indexed — nothing to backfill."
        : `Backfilled ${result.succeeded} memor${result.succeeded === 1 ? "y" : "ies"}. ${result.failed} failed.`
      : null;

  return (
    <div style={{ ...cardStyle, opacity: 1, marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <i className="ti ti-database-search" style={{ fontSize: 16, color: "var(--color-text-secondary)" }} aria-hidden="true" />
        <span style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>Memory backfill</span>
      </div>
      <div style={{ fontSize: 13, color: "var(--color-text-secondary)", paddingLeft: 26, marginBottom: 12 }}>
        {statusText}
      </div>
      <div style={{ paddingLeft: 26, display: "flex", alignItems: "center", gap: 12 }}>
        <button
          onClick={() => backfill.mutate()}
          disabled={!canBackfill}
          style={{
            fontSize: 13,
            padding: "6px 14px",
            borderRadius: "var(--border-radius-md)",
            border: "var(--kuvio-border-default)",
            background: canBackfill ? "var(--color-background-secondary)" : "var(--color-background-primary)",
            color: canBackfill ? "var(--color-text-primary)" : "var(--color-text-tertiary)",
            cursor: canBackfill ? "pointer" : "default",
          }}
        >
          {backfill.isPending ? "Backfilling…" : "Backfill now"}
        </button>
        {resultText && (
          <span style={{ fontSize: 13, color: backfill.isError ? "var(--color-text-danger, #c0392b)" : "var(--color-text-secondary)" }}>
            {resultText}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Sprint 24.9 (S24.9.29) — MCP usage. Owner-only. Lists the top tools by total
 * MCP response size (bytes + estimated tokens) so the owner can see which tools
 * cost connected hosts the most per turn — the prioritisation signal for v1.1
 * shape optimisations. Read-only; powered by `get_tool_response_stats`.
 */
function McpUsageCard({ cardStyle }: { cardStyle: CSSProperties }) {
  const stats = useToolResponseStats(true, 10);
  const data = stats.data;
  const rows = data?.tools ?? [];

  const fmtKb = (bytes: number) => `${(bytes / 1024).toFixed(1)} KB`;

  let bodyText: string | null = null;
  if (stats.isLoading) bodyText = "Loading usage…";
  else if (stats.isError) bodyText = "Couldn't load MCP usage.";
  else if (rows.length === 0) bodyText = "No tool calls recorded yet. Usage appears here once hosts call Kuvio tools.";

  return (
    <div style={{ ...cardStyle, opacity: 1, marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <i className="ti ti-chart-bar" style={{ fontSize: 16, color: "var(--color-text-secondary)" }} aria-hidden="true" />
        <span style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>MCP usage</span>
      </div>
      <div style={{ fontSize: 13, color: "var(--color-text-secondary)", paddingLeft: 26, marginBottom: 12 }}>
        Top tools by response size{data && rows.length > 0 ? ` (${data.totals.calls} calls tracked)` : ""}.
      </div>
      {bodyText ? (
        <div style={{ fontSize: 13, color: "var(--color-text-tertiary)", paddingLeft: 26 }}>{bodyText}</div>
      ) : (
        <div style={{ paddingLeft: 26, display: "flex", flexDirection: "column", gap: 6 }}>
          {rows.map((r) => (
            <div
              key={r.tool_name}
              style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 13 }}
            >
              <span style={{ fontFamily: "var(--font-mono, monospace)", color: "var(--color-text-primary)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {r.tool_name}
              </span>
              <span style={{ color: "var(--color-text-secondary)", minWidth: 64, textAlign: "right" }}>{fmtKb(r.total_bytes)}</span>
              <span style={{ color: "var(--color-text-tertiary)", minWidth: 64, textAlign: "right" }}>
                {r.calls} call{r.calls === 1 ? "" : "s"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Sprint 24.10 (S24.10.21/22) — Context bundles. Lists the Kuvio-blessed context
 * bundles surfaced via `prompts/list` (filtered on `_meta.kuvio_blessed`), each
 * with its description, estimated output tokens, and cache TTL. Educates dogfood
 * users on what a connected host can fetch in one `prompts/get` call. For owners,
 * per-bundle invocation counts are joined in from `get_tool_response_stats`
 * (logged under `prompt:<slug>` names), surfacing which bundles are popular.
 */
function ContextBundlesCard({ cardStyle, isOwner }: { cardStyle: CSSProperties; isOwner: boolean }) {
  const prompts = useMcpPrompts();
  // top_n=100 so every `prompt:*` row is captured among the tracked tools.
  const stats = useToolResponseStats(isOwner, 100);

  const bundles = (prompts.data ?? []).filter((p) => p._meta?.kuvio_blessed === true);
  const callsByName = new Map<string, number>();
  for (const r of stats.data?.tools ?? []) callsByName.set(r.tool_name, r.calls);

  let bodyText: string | null = null;
  if (prompts.isLoading) bodyText = "Loading bundles…";
  else if (prompts.isError) bodyText = "Couldn't load context bundles.";
  else if (bundles.length === 0) bodyText = "No context bundles available.";

  const chip: CSSProperties = {
    fontSize: 11,
    color: "var(--color-text-tertiary)",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-sm, 4px)",
    padding: "1px 6px",
    whiteSpace: "nowrap",
  };

  return (
    <div style={{ ...cardStyle, opacity: 1, marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <i className="ti ti-package" style={{ fontSize: 16, color: "var(--color-text-secondary)" }} aria-hidden="true" />
        <span style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>Context bundles</span>
      </div>
      <div style={{ fontSize: 13, color: "var(--color-text-secondary)", paddingLeft: 26, marginBottom: 12 }}>
        Curated multi-source bundles a connected host fetches in one call (<code>prompts/get</code>) instead of chaining several tool calls.
      </div>
      {bodyText ? (
        <div style={{ fontSize: 13, color: "var(--color-text-tertiary)", paddingLeft: 26 }}>{bodyText}</div>
      ) : (
        <div style={{ paddingLeft: 26, display: "flex", flexDirection: "column", gap: 12 }}>
          {bundles.map((b) => {
            const est = b._meta?.estimated_tokens;
            const ttl = b._meta?.cache_ttl_seconds;
            const calls = callsByName.get(`prompt:${b.name}`);
            return (
              <div key={b.name} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: "var(--font-mono, monospace)", fontSize: 13, color: "var(--color-text-primary)" }}>
                    {b.name}
                  </span>
                  {typeof est === "number" && <span style={chip}>~{est} tok</span>}
                  {typeof ttl === "number" && <span style={chip}>{ttl}s cache</span>}
                  {isOwner && typeof calls === "number" && (
                    <span style={chip}>{calls} call{calls === 1 ? "" : "s"}</span>
                  )}
                </div>
                {b.description && (
                  <div style={{ fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.4 }}>
                    {b.description}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Sprint 23 — Appearance: the workspace's language theme. Five cards, one tap
 * to switch. The picker shows each theme's "addAction" verb + a sample status
 * pill prefix so the user feels the voice difference before committing. Onboarding
 * surfaces the same picker in 23.7; this is the discoverability/change surface.
 */
function AppearanceSection({ cardStyle }: { cardStyle: CSSProperties }) {
  const { theme, setTheme, isSaving } = useLanguageTheme();

  return (
    <div style={{ ...cardStyle, opacity: 1, marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <i className="ti ti-palette" style={{ fontSize: 16, color: "var(--color-text-secondary)" }} aria-hidden="true" />
        <span style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>Appearance</span>
      </div>
      <div style={{ fontSize: 13, color: "var(--color-text-secondary)", paddingLeft: 26, marginBottom: 14 }}>
        Choose how Kuvio names your agents as a group. Pure labels — no functional change.
      </div>
      <div
        role="radiogroup"
        aria-label="Language theme"
        style={{
          paddingLeft: 26,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 10,
        }}
      >
        {LANGUAGE_THEMES.map((key) => (
          <ThemeCard
            key={key}
            theme={key}
            variant="settings"
            selected={theme === key}
            disabled={isSaving}
            onPick={() => {
              if (key === theme) return;
              setTheme(key);
              showToast(`Switched to ${LANGUAGE_THEME_NAMES[key]} theme`);
            }}
          />
        ))}
      </div>
    </div>
  );
}
